import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import { IconButton, TableBody, TableRow, TableCell, TableHead, Box } from '@mui/material';
import { Add, Save, ViewList } from '@mui/icons-material';
import { getErrors } from '@totalsoft/pure-validations-react';
import PropertyDetailListItemComponent from './PropertyDetailListItemComponent';
import PropertyDetailFiltersComponent from './PropertyDetailFiltersComponent';
import { Pagination, Typography, Card } from '@totalsoft/rocket-ui';
import { StyledHeaderCell, StyledTable, StyledTableRow, StyledGrayIcon } from 'components/common/Styles';
import { emptyString } from 'utils/constants';
import { ADMIN_PROPERTIES_WRITE } from 'constants/rightsCodes';
import { usePermission } from 'providers/RightsHooks';

const PropertyDetailListComponent = ({
  filters,
  onChangeFilterValue,
  propertyDetails,
  propertyTypeId,
  onPropertyChanged,
  onAdd,
  onSave,
  onDelete,
  onCancel,
  onEdit,
  validation,
  saveDisabled,
  pager,
  onPageChange,
  onRowsPerPageChange,
  onRefresh,
  loading,
  isNewProperty
}) => {
  const [t] = useTranslation();
  const canWrite = usePermission(ADMIN_PROPERTIES_WRITE);

  return (
    <Card
      icon={ViewList}
      iconColor='primary'
      title={t('Property.PropertyDetails')}
      actions={
        <>
          <IconButton title={t('Tooltips.Add')} onClick={onAdd} disabled={!canWrite} color='primary'>
            <Add />
          </IconButton>
          <IconButton
            color='primary'
            title={t('SaveChanges')}
            onClick={onSave}
            disabled={!canWrite || (validation |> getErrors) !== emptyString || saveDisabled || !propertyDetails.changed}
          >
            <Save />
          </IconButton>
        </>
      }
      subheader={<Typography color='error'>{validation |> getErrors}</Typography>}
    >
      <Prompt when={propertyDetails.changed} message={t('Property.LeavingWithoutSavingProperty')} />
      <PropertyDetailFiltersComponent onChangeFilterValue={onChangeFilterValue} filters={filters} />
      <StyledTable>
        <TableHead>
          <TableRow>
            <StyledHeaderCell>{t('Property.OrderNo')}</StyledHeaderCell>
            <StyledHeaderCell>{t('Property.Code')}</StyledHeaderCell>
            <StyledHeaderCell>{t('Property.PropertyValue')}</StyledHeaderCell>
            <StyledHeaderCell>{t('Property.Description')}</StyledHeaderCell>
            <StyledHeaderCell>{t('Property.Inactive')}</StyledHeaderCell>
            <StyledHeaderCell align='right'>{t('Property.Actions')}</StyledHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <>
            {!isNewProperty && propertyDetails.values.length === 0 && (
              <StyledTableRow>
                <TableCell colSpan={6}>
                  <Box display='flex' gap={1} alignItems='center' justifyContent='center'>
                    <StyledGrayIcon fontSize='small'>error</StyledGrayIcon>
                    <Typography color='textSecondary'>{t('ResultsNotFound')}</Typography>
                  </Box>
                </TableCell>
              </StyledTableRow>
            )}
            {propertyDetails.values.map((item, idx) => {
              return (
                <PropertyDetailListItemComponent
                  key={idx}
                  index={idx}
                  propertyDetail={item}
                  propertyTypeId={propertyTypeId}
                  validation={validation}
                  onPropertyChanged={onPropertyChanged}
                  onDelete={onDelete}
                  onCancel={onCancel}
                  onEdit={onEdit}
                />
              );
            })}
          </>
        </TableBody>
      </StyledTable>
      <Pagination
        loading={loading}
        count={pager.totalCount}
        pageSize={pager.pageSize}
        page={pager.page}
        onRowsPerPageChange={onRowsPerPageChange}
        onPageChange={onPageChange}
        onRefresh={onRefresh}
      />
    </Card>
  );
};

PropertyDetailListComponent.propTypes = {
  filters: PropTypes.object.isRequired,
  onChangeFilterValue: PropTypes.func.isRequired,
  propertyDetails: PropTypes.object.isRequired,
  propertyTypeId: PropTypes.number,
  onPropertyChanged: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  validation: PropTypes.object.isRequired,
  saveDisabled: PropTypes.bool.isRequired,
  pager: PropTypes.object.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  onCancel: PropTypes.func,
  onEdit: PropTypes.func,
  isNewProperty: PropTypes.bool.isRequired
};

export default PropertyDetailListComponent;
